import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subject } from 'rxjs';
import { map, takeUntil, startWith } from 'rxjs/operators';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IpexCommandResponse } from '../models/responseModels/IpexCommandResponse';
import { UrlResolverService } from './url/url-resolver.service';

@Injectable({ providedIn: 'root' })
export class UserActivityService implements OnDestroy {
    private readonly urlResolver = inject(UrlResolverService);
    private readonly baseUrl: string = this.urlResolver.getPcxCommandUrl();
    private readonly updatePersonLastActiveTimeEnpoint: string = `${this.baseUrl}UpdatePersonLastActiveTime`;
    private readonly header: HttpHeaders = new HttpHeaders({
        'Content-Type': 'application/json'
    });

    private lastActiveTimestamp = new BehaviorSubject<number | null>(null);
    public readonly lastActiveTime$: Observable<Date | null> = this.lastActiveTimestamp.pipe(
        map(timestamp => (timestamp ? new Date(timestamp) : null))
    );

    public readonly periodicActiveTime$: Observable<Date | null>;
    private listening = false;
    private readonly events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    private readonly destroy$ = new Subject<void>();

    private boundHandleActivity: () => void;
    private boundHandleVisibilityChange: () => void;

    constructor(private ngZone: NgZone, private httpClient: HttpClient) {
        this.boundHandleActivity = this.handleActivity.bind(this);
        this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

        // Create periodic observable that emits every nth minutes
        this.periodicActiveTime$ = interval(PCXConstants.UserActivity.PERIODIC_ACTIVE_TIME_IN_MILLISECONDS).pipe(
            startWith(0), // Emit immediately
            map(() => {
                const timestamp = this.lastActiveTimestamp.value;
                return timestamp ? new Date(timestamp) : null;
            }),
            takeUntil(this.destroy$)
        );
    }

    startTracking(): void {
        if (this.listening) return;

        this.listening = true;
        this.ngZone.runOutsideAngular(() => {
            this.events.forEach(event =>
                window.addEventListener(event, this.boundHandleActivity, { passive: true, capture: true })
            );
            document.addEventListener('visibilitychange', this.boundHandleVisibilityChange, {
                passive: true,
                capture: true
            });
        });

        this.updateLastActive();
    }

    stopTracking(): void {
        if (!this.listening) return;

        this.listening = false;
        this.events.forEach(event => window.removeEventListener(event, this.boundHandleActivity, { capture: true }));
        document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange, { capture: true });
        this.lastActiveTimestamp.next(null);
    }

    private handleActivity(): void {
        this.ngZone.run(() => this.updateLastActive());
    }

    private handleVisibilityChange(): void {
        if (!document.hidden) {
            this.ngZone.run(() => this.updateLastActive());
        }
    }

    private updateLastActive(): void {
        if (this.listening) {
            // Store timestamp instead of Date object (more memory efficient)
            this.lastActiveTimestamp.next(Date.now());
        }
    }

    isActive(timeoutMinutes: number = 5): boolean {
        const last = this.lastActiveTimestamp.value;
        if (!last) return false;

        const diff = (Date.now() - last) / 60000;
        return diff < timeoutMinutes;
    }

    getLastActiveTime(): Date | null {
        const timestamp = this.lastActiveTimestamp.value;
        return timestamp ? new Date(timestamp) : null;
    }

    getTimeSinceLastActivity(): number {
        const last = this.lastActiveTimestamp.value;
        if (!last) return 0;

        return (Date.now() - last) / 60000;
    }

    updatePersonLastActiveTime(
           ): Observable<IpexCommandResponse<string[]>> {
         const payload = {};
            let url =this.updatePersonLastActiveTimeEnpoint;
            return this.httpClient.post<IpexCommandResponse<string[]>>(url, payload, {
                headers: this.header,
                withCredentials: true
            });
        }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopTracking();
    }
}
